import { Button, Card, CardBody, CardHeader, CardTitle, Pill } from '@/shared/components';
import type { UserResponse } from '@/shared/api';
import { UserInvitationAction } from '../UserInvitationAction';
import { userStatus } from '../userStatus';

interface Props {
  user: UserResponse;
  isSelf: boolean;
  resendPending: boolean;
  onResendInvitation: () => void;
  onResetTwoFactor: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  reactivatePending: boolean;
}

/**
 * Anmeldesicherheit und Zugang.
 *
 * <p>Die destruktive Aktion steht räumlich abgesetzt unter einem eigenen Trennstrich (§11) und
 * nicht neben „Einladung erneut senden" — die beiden werden sonst im Vorbeigehen verwechselt.
 * Es werden nur Aktionen gezeigt, die der Server für diesen Zustand auch annimmt: das erneute
 * Senden gibt es nur für offene Einladungen, das Zurücksetzen nur bei aktiver Zwei-Faktor-
 * Anmeldung und nie am eigenen Konto.</p>
 */
export function UserSecurityCard({
  user,
  isSelf,
  resendPending,
  onResendInvitation,
  onResetTwoFactor,
  onDeactivate,
  onReactivate,
  reactivatePending,
}: Props) {
  const status = userStatus(user);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Sicherheit und Zugang</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-ink text-sm font-medium">Zwei-Faktor-Anmeldung</p>
            <p className="text-ink-soft mt-0.5 text-xs">
              {user.twoFactorEnabled
                ? 'Eingerichtet — die Anmeldung verlangt einen zweiten Faktor.'
                : 'Nicht eingerichtet. Der Workspace kann sie verpflichtend machen.'}
            </p>
          </div>
          <Pill variant={user.twoFactorEnabled ? 'live' : 'warning'} size="sm">
            {user.twoFactorEnabled ? 'Aktiv' : 'Fehlt'}
          </Pill>
        </div>

        {user.twoFactorEnabled && !isSelf && (
          <Button
            variant="ghost"
            className="border-line min-h-11 w-full border"
            onClick={onResetTwoFactor}
          >
            Zwei-Faktor zurücksetzen
          </Button>
        )}

        {status === 'Invited' && (
          <div className="border-line/50 border-t pt-4">
            <p className="text-ink-soft mb-2 text-xs">
              Die Einladung ist noch offen — dieser Benutzer hat sich nie angemeldet.
            </p>
            <UserInvitationAction user={user} />
            <Button
              variant="ghost"
              className="border-line mt-2 min-h-11 w-full border"
              loading={resendPending}
              onClick={onResendInvitation}
            >
              Einladung erneut senden
            </Button>
          </div>
        )}

        <div className="border-line/50 border-t pt-4">
          {isSelf ? (
            // §10: nur Aktionen zeigen, die der Aufrufer haben darf. Eine abgeblendete rote
            // Schaltfläche sieht aus wie ein Fehler und ist trotzdem keine Auskunft.
            <p className="text-ink-soft text-xs">
              Das ist Ihr eigenes Konto — den eigenen Zugang sperrt die Benutzerverwaltung nicht.
            </p>
          ) : user.isActive ? (
            <>
              <p className="text-ink-soft mb-2 text-xs">
                Beendet alle laufenden Sitzungen. Das Konto bleibt erhalten und kann jederzeit
                wieder freigegeben werden.
              </p>
              <Button variant="destructive" className="min-h-11 w-full" onClick={onDeactivate}>
                Zugang sperren
              </Button>
            </>
          ) : (
            <>
              <p className="text-ink-soft mb-2 text-xs">
                Der Zugang ist gesperrt. Eine Freigabe stellt die bisherige Rolle wieder her.
              </p>
              <Button
                variant="brand"
                className="min-h-11 w-full"
                loading={reactivatePending}
                onClick={onReactivate}
              >
                Zugang wieder freigeben
              </Button>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
